type LogLevel = "log" | "info" | "warn" | "error";
type Filter   = "all" | LogLevel;
type LogPart  = { t: "s"; v: string } | { t: "o"; v: unknown; s: string };
type LogEntry = { level: LogLevel; parts: LogPart[]; msg: string; time: string };

const _objs = new Map<number, unknown>();
let   _oid  = 0;

export class LogOverlay
{
  private static readonly MAX_LOGS = 6000;
  private static readonly OBJECT_LOGS = 200;
  private static readonly MAX_PART_CHARS = 4000;

  private static logs: LogEntry[]                 = [];
  private static container: HTMLDivElement | null = null;
  private static pre: HTMLPreElement | null       = null;
  private static visible  = false;
  private static captured = false;
  private static filter: Filter = "all";
  private static text_filter    = "";
  private static filter_btns: Partial<Record<Filter, HTMLButtonElement>> = {};
  private static search_wrap: HTMLDivElement | null  = null;
  private static search_btn:  HTMLButtonElement | null = null;
  private static search_open  = false;

  private static scroll_btn: HTMLButtonElement | null = null;
  private static copy_btn:   HTMLButtonElement | null = null;
  private static at_bottom   = true;

  private static debug_header_provider: ( () => Promise<string> ) | null = null;

  public static setDebugHeaderProvider( fn: () => Promise<string> ): void
  {
    this.debug_header_provider = fn;
  }

  public static async buildLogText(): Promise<string>
  {
    const header = this.debug_header_provider
      ? await this.debug_header_provider().catch( () => "" )
      : "";

    const logs = this.logs
      .map( e =>
      {
        const pfx =
          e.level === "error" ? "[E]" :
          e.level === "warn"  ? "[W]" :
          e.level === "info"  ? "[I]" :
          "[ ]";
        return `${e.time} ${pfx} ${e.msg}`;
      } )
      .join( "\n" );

    return header ? `${header}\n${logs}` : logs;
  }

  private static height_vh       = 35;
  private static readonly MIN_VH = 10;
  private static readonly MAX_VH = 85;

  public static startCapture(): void
  {
    if ( this.captured ) return;
    this.captured = true;

    const orig_log   = console.log.bind( console );
    const orig_info  = console.info.bind( console );
    const orig_warn  = console.warn.bind( console );
    const orig_error = console.error.bind( console );

    console.log   = ( ...a ) => { this.capture( "log",   a ); orig_log(   ...a ); };
    console.info  = ( ...a ) => { this.capture( "info",  a ); orig_info(  ...a ); };
    console.warn  = ( ...a ) => { this.capture( "warn",  a ); orig_warn(  ...a ); };
    console.error = ( ...a ) => { this.capture( "error", a ); orig_error( ...a ); };
  }

  public static show(): void
  {
    if ( this.visible ) return;
    this.visible = true;
    this.build();
    this.renderAll();
  }

  public static hide(): void
  {
    if ( !this.visible ) return;
    this.visible = false;
    this.container?.remove();
    this.container   = null;
    this.pre         = null;
    this.search_wrap = null;
    this.search_btn  = null;
    this.scroll_btn  = null;
    this.at_bottom   = true;
    this.filter_btns = {};
    _objs.clear();
  }

  public static toggle(): void       { this.visible ? this.hide() : this.show(); }
  public static isVisible(): boolean { return this.visible; }

  public static clear(): void
  {
    this.logs = [];
    _objs.clear();
    if ( this.pre ) this.pre.innerHTML = "";
  }

  private static capture( level: LogLevel, args: unknown[] ): void
  {
    const parts: LogPart[] = [];
    const msg_parts: string[] = [];

    for ( const a of args )
    {
      if ( a !== null && a !== undefined && typeof a === "object" )
      {
        let json: string;
        try { json = JSON.stringify( a ) ?? String( a ); } catch { json = String( a ); }
        json = clip( json, this.MAX_PART_CHARS );
        parts.push( { t: "o", v: a, s: json } );
        msg_parts.push( json );
      }
      else
      {
        const s = clip( String( a ), this.MAX_PART_CHARS );
        parts.push( { t: "s", v: s } );
        msg_parts.push( s );
      }
    }

    const msg  = msg_parts.join( " " );
    const d    = new Date();
    const time = `${pad( d.getHours() )}:${pad( d.getMinutes() )}:${pad( d.getSeconds() )}`;
    const entry: LogEntry = { level, parts, msg, time };

    this.logs.push( entry );
    if ( this.logs.length > this.OBJECT_LOGS )
      dropObjects( this.logs[this.logs.length - 1 - this.OBJECT_LOGS] );
    if ( this.logs.length > this.MAX_LOGS )
    {
      const removed = this.logs.shift()!;
      if ( this.visible && this.pre && this.passes( removed ) )
      {
        if ( this.pre.firstChild ) this.pre.firstChild.remove();
        if ( this.pre.firstChild ) this.pre.firstChild.remove();
      }
    }

    if ( this.visible && this.pre && this.passes( entry ) )
      this.appendEntry( entry );
  }

  private static passes( e: LogEntry ): boolean
  {
    if ( this.filter !== "all" && this.filter !== e.level ) return false;
    if ( this.text_filter && !e.msg.toLowerCase().includes( this.text_filter ) ) return false;
    return true;
  }

  private static build(): void
  {
    const wrap = document.createElement( "div" );
    wrap.style.cssText = [
      "position:absolute", "bottom:0", "left:0", "right:0",
      `height:${this.height_vh}vh`,
      "z-index:2147483645",
      "background:rgba(0,0,0,0.92)",
      "border-top:1px solid rgba(255,255,255,0.15)",
      "display:flex", "flex-direction:column",
    ].join( ";" );

    wrap.appendChild( this.buildResizeHandle( wrap ) );
    wrap.appendChild( this.buildHeader() );
    wrap.appendChild( this.buildFilterBar() );
    wrap.appendChild( this.buildSearchBar() );

    const pre = document.createElement( "pre" );
    pre.style.cssText = [
      "overflow-y:auto", "flex:1", "margin:0",
      "padding:4px 8px",
      "white-space:pre-wrap", "word-break:break-all",
      "font:11px/1.4 monospace",
      "position:relative",
      "-webkit-user-select:text", "user-select:text",
    ].join( ";" );
    pre.addEventListener( "scroll", () => this.onPreScroll() );
    pre.addEventListener( "click",  ( ev ) => this.onObjClick( ev ) );
    wrap.appendChild( pre );
    this.pre = pre;

    const copy_btn = document.createElement( "button" );
    copy_btn.textContent = "COPY";
    copy_btn.style.cssText = [
      "position:absolute", "display:none",
      "z-index:2147483646",
      "background:#2a7fff", "color:#fff",
      "border:none", "border-radius:4px",
      "font:700 11px monospace", "padding:4px 10px",
      "cursor:pointer", "pointer-events:auto",
      "-webkit-tap-highlight-color:transparent",
    ].join( ";" );
    copy_btn.addEventListener( "click", ( e ) =>
    {
      e.preventDefault();
      const sel = window.getSelection();
      if ( sel && sel.toString().length > 0 )
      {
        void navigator.clipboard.writeText( sel.toString() ).then( () =>
        {
          copy_btn.textContent = "✓";
          setTimeout( () => { copy_btn.textContent = "COPY"; }, 1000 );
        } );
      }
    } );
    wrap.appendChild( copy_btn );
    this.copy_btn = copy_btn;

    document.addEventListener( "selectionchange", () => this.onSelectionChange() );

    const scroll_btn = document.createElement( "button" );
    scroll_btn.textContent = "↓";
    scroll_btn.style.cssText = [
      "position:absolute", "bottom:10px", "right:12px",
      "z-index:1", "display:none",
      "background:rgba(255,255,255,0.18)", "border:none",
      "color:#ccc", "font:700 16px monospace",
      "width:32px", "height:32px", "border-radius:50%",
      "cursor:pointer", "line-height:32px", "text-align:center",
      "-webkit-tap-highlight-color:transparent",
    ].join( ";" );
    scroll_btn.addEventListener( "click", () =>
    {
      this.at_bottom = true;
      this.updateScrollBtn();
      if ( this.pre ) this.pre.scrollTop = this.pre.scrollHeight;
    } );
    wrap.appendChild( scroll_btn );
    this.scroll_btn = scroll_btn;

    document.documentElement.appendChild( wrap );
    this.container = wrap;
  }

  private static buildResizeHandle( wrap: HTMLDivElement ): HTMLDivElement
  {
    const handle = document.createElement( "div" );
    handle.style.cssText = [
      "height:20px", "display:flex", "align-items:center", "justify-content:center",
      "cursor:ns-resize", "flex-shrink:0", "touch-action:none",
      "background:rgba(255,255,255,0.03)",
    ].join( ";" );

    const bar = document.createElement( "div" );
    bar.style.cssText = "width:44px;height:4px;background:rgba(255,255,255,0.22);border-radius:2px";
    handle.appendChild( bar );

    let start_y = 0, start_height = 0;

    const onMove = ( y: number ) =>
    {
      const dv = ( start_y - y ) / window.innerHeight * 100;
      this.height_vh = Math.min( this.MAX_VH, Math.max( this.MIN_VH, start_height + dv ) );
      wrap.style.height = `${this.height_vh}vh`;
    };

    handle.addEventListener( "touchstart", e => { e.preventDefault(); start_y = e.touches[0].clientY; start_height = this.height_vh; }, { passive: false } );
    handle.addEventListener( "touchmove",  e => { e.preventDefault(); onMove( e.touches[0].clientY ); }, { passive: false } );
    handle.addEventListener( "mousedown",  e =>
    {
      start_y = e.clientY; start_height = this.height_vh;
      const mm = ( ev: MouseEvent ) => onMove( ev.clientY );
      const mu = () => { window.removeEventListener( "mousemove", mm ); window.removeEventListener( "mouseup", mu ); };
      window.addEventListener( "mousemove", mm );
      window.addEventListener( "mouseup",   mu );
    } );

    return handle;
  }

  private static buildHeader(): HTMLDivElement
  {
    const header = document.createElement( "div" );
    header.style.cssText = "display:flex;align-items:center;padding:3px 8px;border-bottom:1px solid rgba(255,255,255,0.1);flex-shrink:0";

    const title = document.createElement( "span" );
    title.textContent = "◆ LOG";
    title.style.cssText = "color:#888;font-size:10px;letter-spacing:1px;flex:1;font-family:monospace";

    const clear_btn = document.createElement( "button" );
    clear_btn.textContent = "CLEAR";
    clear_btn.style.cssText = BTN_STYLE + "margin-right:6px";
    clear_btn.addEventListener( "click", () => this.clear() );

    const copy_all_btn = document.createElement( "button" );
    copy_all_btn.textContent = "COPY ALL";
    copy_all_btn.style.cssText = BTN_STYLE + "margin-right:6px";
    copy_all_btn.addEventListener( "click", () =>
    {
      const text = this.logs
        .filter( e => this.passes( e ) )
        .map( e =>
        {
          const pfx =
            e.level === "error" ? "[E]" :
            e.level === "warn"  ? "[W]" :
            e.level === "info"  ? "[I]" :
            "[ ]";
          return `${e.time} ${pfx} ${e.msg}`;
        } )
        .join( "\n" );
      void navigator.clipboard.writeText( text ).then( () =>
      {
        copy_all_btn.textContent = "✓";
        setTimeout( () => { copy_all_btn.textContent = "COPY ALL"; }, 1500 );
      } );
    } );

    const save_btn = document.createElement( "button" );
    save_btn.textContent = "SAVE";
    save_btn.style.cssText = BTN_STYLE + "margin-right:6px";
    save_btn.addEventListener( "click", () =>
    {
      save_btn.textContent = "…";
      save_btn.style.opacity = "0.5";

      void ( async () =>
      {
        const d    = new Date();
        const ts   = `${d.getFullYear()}${pad( d.getMonth() + 1 )}${pad( d.getDate() )}_${pad( d.getHours() )}${pad( d.getMinutes() )}${pad( d.getSeconds() )}`;

        const header = this.debug_header_provider
          ? await this.debug_header_provider().catch( () => "" )
          : "";

        const logs = this.logs
          .filter( e => this.passes( e ) )
          .map( e =>
          {
            const pfx =
              e.level === "error" ? "[E]" :
              e.level === "warn"  ? "[W]" :
              e.level === "info"  ? "[I]" :
              "[ ]";
            return `${e.time} ${pfx} ${e.msg}`;
          } )
          .join( "\n" );

        const text = header ? `${header}\n${logs}` : logs;

        save_btn.textContent = "SAVE";
        save_btn.style.opacity = "1";

        const cap = ( window as any )?.Capacitor;
        if ( cap?.isNativePlatform?.() )
        {
          void cap?.Plugins?.UtilityPlugin?.shareLogs?.( { text } );
          return;
        }

        const blob = new Blob( [ text ], { type: "text/plain;charset=utf-8" } );
        const url  = URL.createObjectURL( blob );
        const a    = document.createElement( "a" );
        a.href     = url;
        a.download = `logs_${ts}.txt`;
        a.click();
        URL.revokeObjectURL( url );
      } )();
    } );

    const search_toggle = document.createElement( "button" );
    search_toggle.textContent = "⌕";
    search_toggle.title = "Toggle search";
    search_toggle.style.cssText = "background:none;border:none;color:#666;font:700 17px monospace;cursor:pointer;padding:0 6px;line-height:1;-webkit-tap-highlight-color:transparent";
    this.search_btn = search_toggle;
    search_toggle.addEventListener( "click", () => this.toggleSearch() );

    const close_btn = document.createElement( "button" );
    close_btn.textContent = "✕";
    close_btn.style.cssText = "background:none;border:none;color:#777;font:700 18px monospace;cursor:pointer;padding:0 4px;line-height:1;-webkit-tap-highlight-color:transparent";
    close_btn.addEventListener( "click", () => this.hide() );

    header.appendChild( title );
    header.appendChild( clear_btn );
    header.appendChild( copy_all_btn );
    header.appendChild( save_btn );
    header.appendChild( search_toggle );
    header.appendChild( close_btn );
    return header;
  }

  private static toggleSearch(): void
  {
    this.search_open = !this.search_open;
    if ( this.search_wrap )
      this.search_wrap.style.display = this.search_open ? "flex" : "none";
    if ( this.search_btn )
      this.search_btn.style.color = this.search_open ? "#ccc" : "#666";
  }

  private static buildSearchBar(): HTMLDivElement
  {
    const wrap = document.createElement( "div" );
    wrap.style.cssText = "padding:3px 8px;border-bottom:1px solid rgba(255,255,255,0.08);flex-shrink:0;display:none;align-items:center;gap:4px";
    this.search_wrap = wrap;

    const input = document.createElement( "input" );
    input.type        = "text";
    input.placeholder = "search...";
    input.value       = this.text_filter;
    input.style.cssText = [
      "flex:1", "background:rgba(255,255,255,0.07)", "border:1px solid rgba(255,255,255,0.12)",
      "border-radius:3px", "color:#ccc",
      "font-size:16px", "font-family:monospace",
      "padding:2px 6px",
      "outline:none", "-webkit-tap-highlight-color:transparent",
    ].join( ";" );

    input.addEventListener( "input", () =>
    {
      this.text_filter = input.value.toLowerCase().trim();
      this.renderAll();
    } );

    const clear_btn = document.createElement( "button" );
    clear_btn.textContent = "✕";
    clear_btn.style.cssText = "background:none;border:none;color:#555;font:700 14px monospace;cursor:pointer;padding:0 4px;line-height:1;-webkit-tap-highlight-color:transparent";
    clear_btn.addEventListener( "click", () =>
    {
      input.value      = "";
      this.text_filter = "";
      this.renderAll();
    } );

    wrap.appendChild( input );
    wrap.appendChild( clear_btn );
    return wrap;
  }

  private static buildFilterBar(): HTMLDivElement
  {
    const bar = document.createElement( "div" );
    bar.style.cssText = "display:flex;gap:4px;padding:3px 8px;border-bottom:1px solid rgba(255,255,255,0.08);flex-shrink:0";

    const filters: { key: Filter; label: string; color: string }[] = [
      { key: "all",   label: "ALL",  color: "#aaa" },
      { key: "log",   label: "LOG",  color: "#ccc" },
      { key: "info",  label: "INFO", color: "#6cf" },
      { key: "warn",  label: "WARN", color: "#fa0" },
      { key: "error", label: "ERR",  color: "#f55" },
    ];

    for ( const f of filters )
    {
      const btn = document.createElement( "button" );
      btn.textContent = f.label;
      btn.style.cssText = BTN_STYLE + `color:${f.color}`;
      btn.addEventListener( "click", () => this.setFilter( f.key ) );
      this.filter_btns[ f.key ] = btn;
      bar.appendChild( btn );
    }

    this.updateFilterBtnStyles();
    return bar;
  }

  private static onObjClick( ev: MouseEvent ): void
  {
    const target = ( ev.target as Element ).closest( "[data-oid]" ) as HTMLElement | null;
    if ( !target ) return;

    const oid = parseInt( target.dataset[ "oid" ] ?? "-1", 10 );
    const obj = _objs.get( oid );

    if ( target.dataset[ "exp" ] === "1" )
    {
      target.dataset[ "exp" ] = "0";
      target.innerHTML = `▶ ${esc( objPreview( obj ) )}`;
    }
    else
    {
      target.dataset[ "exp" ] = "1";
      target.innerHTML = `▼ ${colorJson( obj, 0 )}`;
    }
  }

  private static setFilter( f: Filter ): void
  {
    this.filter = f;
    this.updateFilterBtnStyles();
    this.renderAll();
  }

  private static updateFilterBtnStyles(): void
  {
    for ( const [ key, btn ] of Object.entries( this.filter_btns ) as [Filter, HTMLButtonElement][] )
    {
      btn.style.opacity     = key === this.filter ? "1" : "0.35";
      btn.style.fontWeight  = key === this.filter ? "700" : "400";
    }
  }

  private static renderAll(): void
  {
    if ( !this.pre ) return;
    _objs.clear();
    const filtered = this.logs.filter( e => this.passes( e ) );
    this.pre.innerHTML = filtered.map( e => this.fmtHtml( e ) ).join( "\n" );
    this.at_bottom = true;
    this.pre.scrollTop = this.pre.scrollHeight;
    this.updateScrollBtn();
  }

  private static appendEntry( entry: LogEntry ): void
  {
    if ( !this.pre ) return;
    const sep = this.pre.innerHTML ? "\n" : "";
    this.pre.insertAdjacentHTML( "beforeend", sep + this.fmtHtml( entry ) );
    this.scrollToBottom();
  }

  private static fmtHtml( e: LogEntry ): string
  {
    const color =
      e.level === "error" ? "#f55" :
      e.level === "warn" ? "#fa0" :
      e.level === "info" ? "#6cf" :
      "#ccc";
    const pfx =
      e.level === "error" ? "[E]" :
      e.level === "warn" ? "[W]" :
      e.level === "info" ? "[I]" :
      "[ ]";

    const content = e.parts.map( p =>
    {
      if ( p.t === "s" ) return esc( p.v );
      const id = _oid++;
      _objs.set( id, p.v );
      const preview = esc( objPreview( p.v ) );
      return `<span data-oid="${id}" data-exp="0" style="color:#4ec9b0;cursor:pointer;border-bottom:1px dotted #4ec9b0">▶ ${preview}</span>`;
    } ).join( " " );

    return `<span style="color:${color}">${e.time} ${pfx} ${content}</span>`;
  }

  private static scrollToBottom(): void
  {
    if ( this.pre && this.at_bottom ) this.pre.scrollTop = this.pre.scrollHeight;
  }

  private static onPreScroll(): void
  {
    if ( !this.pre ) return;
    const THRESHOLD = 24;
    this.at_bottom = this.pre.scrollHeight - this.pre.scrollTop - this.pre.clientHeight < THRESHOLD;
    this.updateScrollBtn();
  }

  private static updateScrollBtn(): void
  {
    if ( this.scroll_btn )
      this.scroll_btn.style.display = this.at_bottom ? "none" : "block";
  }

  private static onSelectionChange(): void
  {
    if ( !this.copy_btn || !this.pre ) return;

    const sel = window.getSelection();
    const text = sel?.toString() ?? "";

    if ( !text.length || !sel?.rangeCount )
    {
      this.copy_btn.style.display = "none";
      return;
    }

    const range = sel.getRangeAt( 0 );
    if ( !this.pre.contains( range.commonAncestorContainer ) )
    {
      this.copy_btn.style.display = "none";
      return;
    }

    const rect = range.getBoundingClientRect();
    const wrap_rect = this.pre.getBoundingClientRect();

    const btn_x = Math.min( rect.left - wrap_rect.left + rect.width / 2 - 25, wrap_rect.width - 60 );
    const btn_y = rect.top - wrap_rect.top - 30;

    this.copy_btn.style.left = `${Math.max( 0, btn_x )}px`;
    this.copy_btn.style.top  = `${Math.max( 4, btn_y )}px`;
    this.copy_btn.style.display = "block";
  }
}

const BTN_STYLE = "background:rgba(255,255,255,0.08);border:none;color:#aaa;font:400 9px monospace;padding:2px 6px;border-radius:3px;cursor:pointer;-webkit-tap-highlight-color:transparent;";

function pad( n: number ): string { return n.toString().padStart( 2, "0" ); }

function esc( s: string ): string
{
  return s.replace( /&/g, "&amp;" ).replace( /</g, "&lt;" ).replace( />/g, "&gt;" );
}

function objPreview( val: unknown ): string
{
  try
  {
    const s = JSON.stringify( val );
    if ( !s ) return String( val );
    return s.length > 80 ? s.slice( 0, 80 ) + "…" : s;
  }
  catch { return String( val ); }
}

function colorJsonValue( val: unknown, depth: number ): string
{
  if ( depth > 8 ) return '<span style="color:#888">…</span>';

  if ( typeof val === "object" && val !== null )
  {
    const empty = Array.isArray( val )
      ? ( val as unknown[] ).length === 0
      : Object.keys( val as object ).length === 0;

    if ( !empty )
    {
      const id = _oid++;
      _objs.set( id, val );
      return `<span data-oid="${id}" data-exp="0" style="color:#4ec9b0;cursor:pointer;border-bottom:1px dotted #4ec9b0">▶ ${esc( objPreview( val ) )}</span>`;
    }
  }
  return colorJson( val, depth );
}

function colorJson( val: unknown, depth: number ): string
{
  if ( depth > 8 ) return '<span style="color:#888">…</span>';

  if ( val === null )      return '<span style="color:#888">null</span>';
  if ( val === undefined ) return '<span style="color:#888">undefined</span>';
  if ( typeof val === "string" )  return `<span style="color:#ce9178">"${esc( val )}"</span>`;
  if ( typeof val === "number" )  return `<span style="color:#b5cea8">${val}</span>`;
  if ( typeof val === "boolean" ) return `<span style="color:#569cd6">${val}</span>`;

  const ind = "  ".repeat( depth + 1 );
  const end = "  ".repeat( depth );

  if ( Array.isArray( val ) )
  {
    if ( val.length === 0 ) return "<span style=\"color:#ccc\">[]</span>";
    const items = val.map( v => `\n${ind}${colorJsonValue( v, depth + 1 )}` ).join( "," );
    return `[${items}\n${end}]`;
  }

  if ( typeof val === "object" )
  {
    const keys = Object.keys( val as object );
    if ( keys.length === 0 ) return "<span style=\"color:#ccc\">{}</span>";
    const entries = keys.map( k =>
      `\n${ind}<span style="color:#9cdcfe">${esc( k )}</span>: ${colorJsonValue( ( val as Record<string, unknown> )[ k ], depth + 1 )}`
    ).join( "," );
    return `{${entries}\n${end}}`;
  }

  return esc( String( val ) );
}

function clip( s: string, max: number ): string
{
  return s.length > max ? `${s.slice( 0, max )}…` : s;
}

function dropObjects( entry: LogEntry ): void
{
  for ( let i = 0; i < entry.parts.length; i++ )
  {
    const part = entry.parts[i];
    if ( part.t === "o" )
      entry.parts[i] = { t: "s", v: part.s };
  }
}
